import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { catchError, map, Observable, shareReplay, switchMap, take, tap, throwError } from 'rxjs';
import { ToastService } from 'shared/services';
import { VisualsListingsService } from 'visuals/services/visual-listings.service';

const VISUALS_API_URL = environment.BASE_URL + 'visualManagementApi';

@Injectable({ providedIn: 'root' })
export class VisualsManagementApiInterceptor implements HttpInterceptor {
    private warmupInProgress$: Observable<void> | null = null;
    private activeWarmupSubscribers = 0;

    constructor(
        private toastService: ToastService,
        private visualsListingsService: VisualsListingsService,
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!req.url.includes(VISUALS_API_URL)) {
            return next.handle(req);
        }

        return this.handleRequest(req, next);
    }

    private handleRequest(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            map((event) => {
                if (event instanceof HttpResponse) {
                    // For visualsManagementApi, the needed data by the frontend is always wrapped inside the "data" field of the response.
                    // (unless it is a stream - blob / image / file / etc. )
                    // and basically pass that to the consuming services / httpclients
                    const body = event.body;

                    if (body && body?.warnings && Array.isArray(body.warnings) && body.warnings.length > 0) {
                        const message = body.warnings.join(' | ');
                        this.toastService.showToast(message, 'warning', undefined, 10000);
                    }

                    if (body && body.data !== undefined) {
                        return event.clone({ body: body.data });
                    }
                }
                return event;
            }),
            catchError((err: HttpErrorResponse) => {
                // 424 as per business logic states that "the listing has no ice id"
                // 1..n requests are made when a visuals page is opened, and if 424 is encountered
                // the warmup-listing endpoint is called so the listing is created in ice
                // then the previous 1..n failed requests (that have 424 status code response) are retried
                if (err.status === 424) {
                    return this.handleWarmupAndRetry(req, next);
                }

                const errorBodyFromApi = err?.error;

                if (
                    errorBodyFromApi &&
                    errorBodyFromApi?.warnings &&
                    Array.isArray(errorBodyFromApi.warnings) &&
                    errorBodyFromApi.warnings.length > 0
                ) {
                    const message = errorBodyFromApi.warnings.join(' | ');
                    this.toastService.showToast(message, 'warning', undefined, 10000);
                }

                if (errorBodyFromApi.error?.message) {
                    Object.defineProperty(err, 'message', { value: errorBodyFromApi.error.message });
                }
                return throwError(() => err);
            }),
        );
    }

    private handleWarmupAndRetry(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.warmupInProgress$) {
            this.warmupInProgress$ = this.visualsListingsService.warmupListing().pipe(
                take(1),
                tap({
                    subscribe: () => this.activeWarmupSubscribers++,
                    finalize: () => {
                        this.activeWarmupSubscribers--;
                        if (this.activeWarmupSubscribers <= 0) {
                            this.resetWarmupState();
                        }
                    },
                }),
                shareReplay(1),
            );
        }

        return this.warmupInProgress$.pipe(switchMap(() => this.handleRequest(req, next)));
    }

    public resetWarmupState() {
        this.warmupInProgress$ = null;
        this.visualsListingsService.warmupEnded.set(true);
    }
}
